import styled from 'styled-components'

import { Array } from '@blade/std-lib'
import { Typography } from '@blade/ui-kit'
import { TransactionAgeCell } from '@root/entities/network/ui'
import { FilterByMaker } from '@root/features/trade/filters'
import { MarkerCapShortFormatted } from '@root/shared/core/format'

import { TradeAmountChartCell } from './trade-amount-chart-cell'
import { TradeMakerCell } from './trade-maker-cell'
import { TradeTotalCell } from './trade-total-cell'
import { TradeTotalHeader } from './trade-total-header'

import type { PairModel } from '@root/entities/pair'
import type { Trade } from '@root/entities/trade'
import type { TradeTableColumnId } from '@root/features/trade/tables/trades-table/atoms/reatom-trades-table'
import type { Column } from '@root/shared/ui-kit'

export const createTradeTableColumnsCompact = ({
  pair,
}: {
  pair: PairModel | undefined
}) => {
  const columns: Array<Column<Trade.Trade, TradeTableColumnId> | undefined> = [
    {
      id: 'timestamp' as const,
      header: ({ t }) => (
        <TradeHeaderCompactCell $index={'first'}>
          {t('common.age')}
        </TradeHeaderCompactCell>
      ),
      render: ({ value }) => (
        <TradeHeaderCompactCell $index={'first'}>
          <TransactionAgeCell
            network={value.network}
            timestamp={value.timestamp}
            transactionHash={value.transactionHash}
          />
        </TradeHeaderCompactCell>
      ),
      minWidth: 50,
      sortable: true,
    },
    {
      id: 'mcap' as const,
      header: ({ t }) => (
        <TradeHeaderCompactCell>
          {t('common.mcap-short')}
        </TradeHeaderCompactCell>
      ),
      render: ({ value }) => (
        <TradeHeaderCompactCell>
          <TypeTypography>
            <MarkerCapShortFormatted mcap={value.marketCapUsd} />
          </TypeTypography>
        </TradeHeaderCompactCell>
      ),
      justify: 'center',
      minWidth: 60,
    },
    {
      id: 'total-usd' as const,
      header: (props) => (
        <TradeHeaderCompactCell>
          <TradeTotalHeader {...props} pair={pair} currencyType="usd" />
        </TradeHeaderCompactCell>
      ),
      render: ({ value }) => (
        <>
          <TradeAmountChartCellCompact value={value} />
          <TradeHeaderCompactCell>
            <TradeTotalCell pair={pair} value={value} fixedCurrency="usd" />
          </TradeHeaderCompactCell>
        </>
      ),
      getValue: (value) => value.totalUsd,
      minWidth: 60,
      filterable: true,
      justify: 'center',
    },
    {
      id: 'maker',
      header: ({ filter, setColumnFilter, clearColumnFilter, columnId }) => (
        <TradeHeaderCompactCell $index={'last'}>
          <FilterByMaker
            filter={filter}
            setColumnFilter={setColumnFilter}
            clearColumnFilter={clearColumnFilter}
            columnId={columnId}
          />
        </TradeHeaderCompactCell>
      ),
      render: (props) => (
        <TradeHeaderCompactCell $index={'last'}>
          <TradeMakerCell {...props} pair={pair} withTags={false} />
        </TradeHeaderCompactCell>
      ),
      getValue: (value) => value.maker.address,
      minWidth: 165,
      justify: 'end',
      filterable: true,
    },
  ]
  return Array.compact(columns)
}

const TradeHeaderCompactCell = styled.div<{ $index?: 'first' | 'last' }>`
  padding-inline-start: ${(x) => (x.$index === 'first' ? '10px' : '5px')};
  padding-inline-end: ${(x) => (x.$index === 'last' ? '10px' : '5px')};
`

const TypeTypography = styled(Typography)`
  color: var(--row-color);
`

const TradeAmountChartCellCompact = styled(TradeAmountChartCell)`
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: auto;
  pointer-events: none;
`
